import clsx from 'clsx';
import React, { FCWithChildren, useState } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import styles from './tooltip.module.scss';

type AnimatedTooltipProps = {
  in: boolean;
  direction?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
} & React.HTMLProps<HTMLDivElement>;

const AnimatedTooltip: FCWithChildren<AnimatedTooltipProps> = (props) => {
  const { in: inProp, children, direction, delay = 300, ...rest } = props;

  return (
    <Transition
      in={inProp}
      timeout={delay}
      mountOnEnter
      unmountOnExit
    >
      {(state: TransitionStatus) => (
        <div
          style={{
            opacity: state === 'entered' ? 1 : 0,
          }}
          {...rest}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};

export type TooltipProps = {
  tooltipContent: React.JSX.Element | string;
  direction?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
} & React.HTMLProps<HTMLDivElement>;

const Tooltip: FCWithChildren<TooltipProps> = (props) => {
  const { tooltipContent, direction = 'top', delay = 300, children, disabled, ...rest } = props;

  const { className, ...restOfProps } = rest;

  const [active, setActive] = useState<boolean>(false);

  let timeout;

  const showTip = () => {
    timeout = setTimeout(() => {
      setActive(true);
    }, delay);
  };

  const hideTip = () => {
    clearInterval(timeout);
    setActive(false);
  };

  return (
    <div
      className={clsx(
        styles.TooltipWrapper,
        ['top', 'bottom'].includes(direction) && styles.vertical,
        styles[className],
      )}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
    >
      {children}
      {!disabled && (
        <AnimatedTooltip
          {...restOfProps}
          in={active}
          direction={direction}
          delay={delay}
          className={clsx(styles.TooltipTip, styles[direction], styles[className])}
        >
          {typeof tooltipContent === 'string' ? <span>{tooltipContent}</span> : tooltipContent}
        </AnimatedTooltip>
      )}
    </div>
  );
};

export default Tooltip;
